// react
import React, { useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// components
import { Container, Icon, Loader } from '@/components/atoms';
import { LoadingTransition } from '@/components/atoms/LoadingTransition/LoadingTransition';

// react-aria
import { useTextField } from 'react-aria';

// radix
import * as RadixSearch from '@radix-ui/react-dialog';

/**
 * ResultIcon is a helper component that renders the icon for the result.
 */
const ResultIcon = () => (
  <ResultIconWrapper>
    <ResultIconHeader />
    <ResultIconBody>
      <TextLine />
      <TextLine />
      <TextLine isShort />
    </ResultIconBody>
  </ResultIconWrapper>
);

const ResultIconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 28px;
  min-width: 28px;
  max-width: 28px;
`;

const ResultIconHeader = styled.div`
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
  border: 1px solid ${(props) => props.theme.color.neutral[60]};
  background: ${(props) => props.theme.color.neutral[50]};
  height: 15px;
`;

const ResultIconBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[2]};
  border-bottom-left-radius: 3px;
  border-bottom-right-radius: 3px;
  border: 1px solid ${(props) => props.theme.color.neutral[60]};
  border-top: none;
  padding: ${(props) => props.theme.spacing[4]};
  height: 17px;
  box-sizing: border-box;
`;

const TextLine = styled.div<{ isShort?: boolean }>`
  width: ${(props) => (props.isShort ? '60%' : '100%')};
  border-bottom: 1px solid ${(props) => props.theme.color.neutral[70]};
`;

/**
 * RadixSearch opens a dialog window over the current page.
 */
export const Search = ({
  id = 'search-input',
  trigger,
  isOpen,
  setIsOpen,
  value,
  isLoading,
  onChange,
  isDisabled,
  placeholder,
  ariaLabel,
  quickResultsHeader,
  quickResults,
  isEasterEgg,
  ...props
}: SearchProps) => {
  // hooks/
  const ref = useRef<HTMLInputElement>(null);
  let { inputProps } = useTextField(
    { ...props, isDisabled, placeholder, id, type: 'text' },
    ref,
  );

  return (
    <>
      <SearchTrigger onClick={() => setIsOpen?.(true)}>
        <SearchPlaceholder>Search</SearchPlaceholder>
        <Icon icon='search' fontSize={16} />
      </SearchTrigger>
      <RadixSearch.Root
        open={isOpen}
        onOpenChange={() => setIsOpen && setIsOpen(false)}
      >
        {trigger && (
          <RadixSearch.Trigger asChild>{trigger}</RadixSearch.Trigger>
        )}
        <RadixSearch.Portal>
          <SearchOverlay />
          <SearchContent>
            <SearchCloseWrapper aria-label='Close'>
              <SearchClose aria-label='Close'>
                <CloseIcon aria-hidden>
                  <Icon icon='xmark' fontSize={16} />
                </CloseIcon>
              </SearchClose>
            </SearchCloseWrapper>
            <Container>
              <SearchWrapper>
                <SearchInput
                  {...inputProps}
                  ref={ref}
                  value={value}
                  onChange={(e) => onChange?.(e.target.value)}
                  aria-label={ariaLabel}
                  autoFocus
                />
                <SubText>Enter a keyword or general term</SubText>
                <QuickResultHeader>{quickResultsHeader}</QuickResultHeader>
                <LoadingTransition
                  speed={100}
                  isLoading={isLoading}
                  loading={<Loader isEasterEgg={isEasterEgg} />}
                  content={
                    <QuickResultWrapper>
                      {quickResults?.map((quickResult) => (
                        <QuickResultItem
                          key={quickResult.title}
                          to={quickResult?.to || ''}
                        >
                          <QuickResultLeft>
                            <ResultIcon />
                            <QuickResultTitle>
                              {quickResult.title}
                            </QuickResultTitle>
                          </QuickResultLeft>
                          {quickResult.category && (
                            <QuickResultCategory>
                              {quickResult.category}
                            </QuickResultCategory>
                          )}
                        </QuickResultItem>
                      ))}
                      {quickResults?.length === 0 && (
                        <QuickResultItem
                          to='/'
                          onClick={(e) => e.preventDefault()}
                        >
                          <QuickResultTitle>No results found</QuickResultTitle>
                        </QuickResultItem>
                      )}
                    </QuickResultWrapper>
                  }
                />
              </SearchWrapper>
            </Container>
          </SearchContent>
        </RadixSearch.Portal>
      </RadixSearch.Root>
    </>
  );
};

const SearchTrigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${(props) => props.theme.color.neutral[70]};
  padding: ${(props) => props.theme.spacing[8]}
    ${(props) => props.theme.spacing[12]};
  border-radius: 99999px;
  width: 200px;
  color: ${(props) => props.theme.color.neutral[80]};
  cursor: pointer;
`;

const SearchPlaceholder = styled.div`
  font: ${(props) => props.theme.typography.body.sm.regular};
  color: ${(props) => props.theme.color.neutral[50]};
`;

const SearchOverlay = styled(RadixSearch.Overlay)`
  background: ${(props) => props.theme.color.overlay};
  position: fixed;
  inset: 0;
  animation: overlayShow 400ms cubic-bezier(0.16, 1, 0.3, 1);
  @keyframes overlayShow {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const SearchContent = styled(RadixSearch.Content)`
  background: ${(props) => props.theme.color.neutral[10]};
  box-shadow: ${(props) => props.theme.elevation[6]};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  min-height: 320px;
  max-height: 90vh;
  box-sizing: border-box;
  overflow-y: hidden;
  &:focus {
    outline: none;
  }
  animation: contentShow 400ms cubic-bezier(0.16, 1, 0.3, 1);
  transform: translate(0%, 0%);
  @keyframes contentShow {
    from {
      transform: translate(0%, -100%);
    }
    to {
      transform: translate(0%, 0%);
    }
  }
`;

const SearchCloseWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

const SearchClose = styled(RadixSearch.Close)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${(props) => props.theme.spacing[24]};
  border: none;
  background: none;
  cursor: pointer;
  color: ${(props) => props.theme.color.neutral[80]};
  transition-duration: 200ms;
  &:hover {
    transform: scale(1.2);
  }
  &:focus {
    outline: none;
  }
`;

const CloseIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  min-width: 28px;
  height: 28px;
  min-height: 28px;
  background: ${(props) => props.theme.color.neutral[30]};
  color: ${(props) => props.theme.color.neutral[90]};
  border-radius: 99999px;
`;

const SearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${(props) => props.theme.spacing[32]} 0;
`;

const SearchInput = styled.input`
  all: unset;
  display: block;
  border-left: 4px solid ${(props) => props.theme.color.neutral[100]};
  height: 80px;
  padding-left: ${(props) => props.theme.spacing[24]};
  overflow: visible;
  font: ${(props) => props.theme.typography.heading['6xl']};
  &::placeholder {
    color: ${(props) => props.theme.color.neutral[50]};
  }
`;

const SubText = styled.div`
  font: ${(props) => props.theme.typography.body.md.medium};
  color: ${(props) => props.theme.color.neutral[50]};
  margin-top: ${(props) => props.theme.spacing[32]};
  margin-left: ${(props) => props.theme.spacing[24]};
`;

const QuickResultHeader = styled.div`
  font: ${(props) => props.theme.typography.body.sm.semibold};
  color: ${(props) => props.theme.color.neutral[100]};
  margin-top: ${(props) => props.theme.spacing[32]};
  margin-bottom: ${(props) => props.theme.spacing[20]};
`;

const QuickResultWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 250px;
  overflow-y: auto;
`;

const QuickResultItem = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(props) => props.theme.spacing[16]}
    ${(props) => props.theme.spacing[16]};
  cursor: pointer;
  border-top: 1px solid ${(props) => props.theme.color.neutral[50]};
  user-select: none;
  text-decoration: none;
  &:last-child {
    border-bottom: 1px solid ${(props) => props.theme.color.neutral[50]};
  }
  transition-duration: 200ms;
  &:hover {
    background: ${(props) => props.theme.color.neutral[30]};
  }
`;

const QuickResultLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
`;

const QuickResultTitle = styled.div`
  font: ${(props) => props.theme.typography.body.md.medium};
  color: ${(props) => props.theme.color.neutral[100]};
`;

const QuickResultCategory = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[70]};
  text-align: right;
`;

export interface QuickResult {
  title: string;
  category?: string;
  to?: string;
}

interface SearchProps {
  id?: string;
  trigger?: React.ReactNode;
  children?: React.ReactNode;
  isOpen?: boolean;
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  value?: string;
  onChange?: (value: string) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  quickResultsHeader?: string;
  quickResults?: QuickResult[];
  isEasterEgg?: boolean;
}

Search.displayName = 'Search';

export default Search;
